/**
 * Courier Orders — partner deliveries and their First Delivery state.
 *
 * Deliberately built to feel like Dispatch (search + status-count filter bar +
 * a route map in a right-side drawer), because that is the screen every admin
 * already knows how to read. Same underlying data and actions as before — this
 * is a layout/interaction pass, not a feature change.
 *
 * Stalled orders are pinned to the top, because the whole reason this screen exists
 * is the order that is quietly going wrong. An order sitting at DISPATCHING with a
 * First Delivery id and no driver means FD could not find a courier — nothing
 * announces that, and the only fix is a human phoning the restaurant.
 */
import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react'
import { useQuery, useMutation, gql } from '@apollo/client'
import MapGL, { Marker, Source, Layer } from 'react-map-gl'
import 'mapbox-gl/dist/mapbox-gl.css'
import {
  Box, Container, Typography, Paper, Chip, IconButton, Button, CircularProgress,
  Alert, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
  Divider, Tooltip, TextField, InputAdornment, ToggleButton, ToggleButtonGroup,
  Stack, Avatar, Drawer,
} from '@mui/material'
import DataTable from 'react-data-table-component'
import RefreshIcon from '@mui/icons-material/Refresh'
import LocalShippingIcon from '@mui/icons-material/LocalShipping'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import SearchIcon from '@mui/icons-material/Search'
import CloseIcon from '@mui/icons-material/Close'
import StorefrontIcon from '@mui/icons-material/Storefront'
import PersonIcon from '@mui/icons-material/Person'
import ConfigurableValues from '../config/constants'
import { getDirections } from '../utils/mapbox'

const COURIER_ORDERS = gql`
  query CourierOrders($status: String, $limit: Int, $offset: Int) {
    courierOrders(status: $status, limit: $limit, offset: $offset) {
      total
      orders {
        _id merchantName merchantAddress merchantLatitude merchantLongitude merchantProvider
        customerName customerPhone externalOrderNumber
        orderKind status dropoffAddress dropoffLatitude dropoffLongitude promiseTime dispatchAt
        subtotal deliveryFee processingFee tax tip total
        fdOrderId fdExternalId fdDriverName fdTrackerLink
        dispatchAttempts lastFdEventAt failureReason createdAt
        items { _id position title quantity unitPrice modifiers }
      }
    }
  }
`
const COURIER_HEALTH = gql`query CourierHealth { courierHealth }`
const DISPATCH = gql`
  mutation Dispatch($id: ID!) {
    courierDispatchOrder(id: $id) { ok skipped fdOrderId reason order { _id status fdOrderId } }
  }
`
const CANCEL = gql`
  mutation Cancel($id: ID!, $reason: String) {
    courierCancelOrder(id: $id, reason: $reason) { ok reason order { _id status } }
  }
`

// A generous ceiling for a brand-new, low-volume pipeline — fetched once so every
// filter tab can show a live count the way Dispatch's do, the same trade Dispatch
// itself makes for its own "active" bucket. Revisit with real server pagination
// once a single merchant alone clears this in a day.
const FETCH_ALL_LIMIT = 200

// Same labels/keys as the Order-system field on Courier Merchants — one vocabulary
// for "which platform" across both screens.
const PROVIDER_LABELS = { EDINING: 'eDiningExpress', MENUSIFU: 'MenuSifu', OTHER: 'Other' }

const statusStyle = {
  DRAFT:        { bg: '#f1f5f9', color: '#475569' },
  NEEDS_REVIEW: { bg: '#fef3c7', color: '#92400e' },
  READY:        { bg: '#e0f2fe', color: '#0369a1' },
  SCHEDULED:    { bg: '#e0e7ff', color: '#3730a3' },
  DISPATCHING:  { bg: '#fef9c3', color: '#713f12' },
  ASSIGNED:     { bg: '#dbeafe', color: '#1e40af' },
  PICKED_UP:    { bg: '#e0f2fe', color: '#0369a1' },
  EN_ROUTE:     { bg: '#ede9fe', color: '#5b21b6' },
  DELIVERED:    { bg: '#dcfce7', color: '#14532d' },
  CANCELLED:    { bg: '#fee2e2', color: '#991b1b' },
  FAILED:       { bg: '#fee2e2', color: '#991b1b' },
}

// Same 6 buckets the page has always had — this is a restyle, not a redefinition.
// "Live" carries no server-side status filter on purpose (it always has not); it is
// the one view that shows everything, stalled rows sorted to the top.
const STATUS_FILTERS = [
  { key: 'LIVE', label: 'Live', status: null },
  { key: 'DISPATCHING', label: 'Dispatching', status: 'DISPATCHING' },
  { key: 'READY', label: 'Ready', status: 'READY' },
  { key: 'DELIVERED', label: 'Delivered', status: 'DELIVERED' },
  { key: 'FAILED', label: 'Failed', status: 'FAILED' },
  { key: 'CANCELLED', label: 'Cancelled', status: 'CANCELLED' },
]

const PAGE_SIZE = 25
const money = (n) => (n == null ? '—' : `$${Number(n).toFixed(2)}`)
const when = (s) => (s ? new Date(s).toLocaleString() : '—')
const minsSince = (s) => (s ? Math.round((Date.now() - new Date(s).getTime()) / 60000) : null)

const StatusChip = ({ status }) => (
  <Chip size="small" label={status} sx={{ ...statusStyle[status], fontWeight: 700, fontSize: '0.7rem', height: 22 }} />
)

const ROUTE_LAYER_STYLE = {
  id: 'courier-route-line', type: 'line',
  layout: { 'line-join': 'round', 'line-cap': 'round' },
  paint: { 'line-color': '#0ea5e9', 'line-width': 4, 'line-opacity': 0.9 },
}
const mapPinStyle = (bg, size = 24) => ({
  width: size, height: size, borderRadius: '50%', background: bg, color: '#fff',
  border: '2px solid #fff', boxShadow: '0 1px 4px rgba(0,0,0,0.4)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700,
})

// Pickup (merchant) → dropoff (customer). Same map component as Dispatch's, minus
// the live-driver subscription — First Delivery doesn't push us a location stream,
// only the periodic ETA/status fields already shown in the First Delivery section.
const RouteMap = ({ order, mapboxToken }) => {
  const rest = (order.merchantLatitude != null && order.merchantLongitude != null)
    ? { lat: order.merchantLatitude, lng: order.merchantLongitude } : null
  const cust = (order.dropoffLatitude != null && order.dropoffLongitude != null)
    ? { lat: order.dropoffLatitude, lng: order.dropoffLongitude } : null
  const mapRef = useRef(null)
  const [routeGeojson, setRouteGeojson] = useState(null)

  useEffect(() => {
    if (!mapboxToken || !rest || !cust) { setRouteGeojson(null); return }
    let cancelled = false
    getDirections(rest, cust, mapboxToken).then((res) => { if (!cancelled && res) setRouteGeojson(res.geojson) }).catch(() => {})
    return () => { cancelled = true }
  }, [mapboxToken, rest?.lat, rest?.lng, cust?.lat, cust?.lng])

  const fitBounds = useCallback(() => {
    const map = mapRef.current
    if (!map) return
    const pts = [rest, cust].filter(Boolean)
    if (pts.length === 0) return
    if (pts.length === 1) { map.easeTo({ center: [pts[0].lng, pts[0].lat], zoom: 13 }); return }
    const lngs = pts.map((p) => p.lng), lats = pts.map((p) => p.lat)
    map.fitBounds([[Math.min(...lngs), Math.min(...lats)], [Math.max(...lngs), Math.max(...lats)]], { padding: 30, duration: 400 })
  }, [rest?.lat, rest?.lng, cust?.lat, cust?.lng])
  useEffect(() => { fitBounds() }, [fitBounds])

  if (!mapboxToken || (!rest && !cust)) {
    return (
      <Box sx={{ height: 200, backgroundColor: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 1 }}>
        <Typography variant="caption" color="text.secondary">
          {!rest && !cust ? 'No coordinates to map yet' : 'Map unavailable'}
        </Typography>
      </Box>
    )
  }
  const center = rest || cust
  return (
    <Box sx={{ height: 200, borderRadius: 1, overflow: 'hidden', border: '1px solid #e5e7eb' }}>
      <MapGL
        ref={mapRef} mapboxAccessToken={mapboxToken}
        initialViewState={{ longitude: center.lng, latitude: center.lat, zoom: 13 }}
        style={{ width: '100%', height: '100%' }} mapStyle="mapbox://styles/mapbox/streets-v12"
        onLoad={fitBounds}
      >
        {rest && <Marker longitude={rest.lng} latitude={rest.lat} anchor="center"><div style={mapPinStyle('#D32F2F')}>R</div></Marker>}
        {cust && <Marker longitude={cust.lng} latitude={cust.lat} anchor="center"><div style={mapPinStyle('#1d4ed8')}>C</div></Marker>}
        {routeGeojson && <Source id="courier-route" type="geojson" data={routeGeojson}><Layer {...ROUTE_LAYER_STYLE} /></Source>}
      </MapGL>
    </Box>
  )
}

const OrderDrawer = ({ order, stallLabels, mapboxToken, onClose, onDispatch, onCancel, dispatching, cancelling }) => {
  if (!order) return null
  return (
    <Drawer anchor="right" open={!!order} onClose={onClose} PaperProps={{ sx: { width: { xs: '100%', sm: 480 } } }}>
      <Box sx={{ p: 2, borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
        <Box sx={{ flex: 1 }}>
          <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {order.merchantName || 'Unknown merchant'} <Typography component="span" variant="h6" color="text.secondary" sx={{ fontWeight: 700 }}>#{order.externalOrderNumber || order._id}</Typography>
            </Typography>
            <StatusChip status={order.status} />
            <Chip size="small" label={order.orderKind} sx={{ height: 22, fontSize: '0.7rem' }} />
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            courier order #{order._id}
            {order.fdExternalId ? ` · FD external_id ${order.fdExternalId}` : ''}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
      </Box>

      <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
        <Stack gap={2}>
          {stallLabels && stallLabels.length > 0 && (
            <Alert severity="warning">{stallLabels.map((l, i) => <div key={i}>{l}</div>)}</Alert>
          )}
          {order.failureReason && <Alert severity="error">{order.failureReason}</Alert>}

          <RouteMap order={order} mapboxToken={mapboxToken} />

          <Box>
            <Typography variant="overline" color="text.secondary">Merchant</Typography>
            <Stack direction="row" gap={1.5} alignItems="center" sx={{ mt: 0.5 }}>
              <Avatar sx={{ width: 32, height: 32, bgcolor: '#fef2f2', color: '#D32F2F' }}><StorefrontIcon fontSize="small" /></Avatar>
              <Box sx={{ flex: 1 }}>
                <Stack direction="row" alignItems="center" gap={1}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{order.merchantName || '—'}</Typography>
                  {order.merchantProvider && (
                    <Chip size="small" label={PROVIDER_LABELS[order.merchantProvider] || order.merchantProvider} sx={{ height: 18, fontSize: '0.65rem', bgcolor: '#f1f5f9' }} />
                  )}
                </Stack>
                {order.merchantAddress && <Typography variant="caption" color="text.secondary">{order.merchantAddress}</Typography>}
              </Box>
            </Stack>
          </Box>

          <Box>
            <Typography variant="overline" color="text.secondary">Customer</Typography>
            <Stack direction="row" gap={1.5} alignItems="center" sx={{ mt: 0.5 }}>
              <Avatar sx={{ width: 32, height: 32 }}><PersonIcon fontSize="small" /></Avatar>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{order.customerName || '—'}</Typography>
                <Typography variant="caption" color="text.secondary">{order.customerPhone || 'no phone'}</Typography>
              </Box>
            </Stack>
          </Box>

          <Box>
            <Typography variant="overline" color="text.secondary">Dropoff Address</Typography>
            <Typography variant="body2" sx={{ mt: 0.5 }}>{order.dropoffAddress || '—'}</Typography>
          </Box>

          <Box>
            <Typography variant="overline" color="text.secondary">Timing</Typography>
            <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.5 }}>
              <Typography variant="body2" color="text.secondary">Due</Typography>
              <Typography variant="body2">{when(order.promiseTime)}</Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">Scheduled dispatch</Typography>
              <Typography variant="body2">{when(order.dispatchAt)}</Typography>
            </Stack>
          </Box>

          <Box>
            <Typography variant="overline" color="text.secondary">Driver / First Delivery</Typography>
            <Stack direction="row" gap={1.5} alignItems="center" sx={{ mt: 0.5 }}>
              <Avatar sx={{ width: 32, height: 32, bgcolor: order.fdDriverName ? '#eff6ff' : undefined }}><LocalShippingIcon fontSize="small" /></Avatar>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {order.fdDriverName || (order.fdOrderId ? 'None yet' : 'Not dispatched')}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {order.fdOrderId ? `order ${order.fdOrderId}` : 'no First Delivery order'}
                  {order.dispatchAttempts ? ` · ${order.dispatchAttempts} attempt(s)` : ''}
                  {' · last event '}{order.lastFdEventAt ? `${minsSince(order.lastFdEventAt)}m ago` : 'never'}
                </Typography>
              </Box>
            </Stack>
            {order.fdTrackerLink && (
              <Button size="small" endIcon={<OpenInNewIcon />} href={order.fdTrackerLink} target="_blank" rel="noreferrer" sx={{ textTransform: 'none', mt: 0.5, pl: 0 }}>
                Live tracker
              </Button>
            )}
          </Box>

          <Divider />

          <Box>
            <Typography variant="overline" color="text.secondary">Items</Typography>
            <Stack gap={1} sx={{ mt: 0.5 }}>
              {(order.items || []).map((it) => {
                let mods = []
                try { mods = it.modifiers ? JSON.parse(it.modifiers) : [] } catch { mods = [] }
                return (
                  <Box key={it._id} sx={{ p: 1.25, borderRadius: 1, backgroundColor: '#f9fafb', border: '1px solid #e5e7eb' }}>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{it.quantity}× {it.title}</Typography>
                      <Typography variant="body2">{money(it.unitPrice)}</Typography>
                    </Stack>
                    {mods.map((m, j) => (
                      <Typography key={j} variant="caption" color="text.secondary" display="block">– {m.title}{m.price != null ? ` (${money(m.price)})` : ''}</Typography>
                    ))}
                  </Box>
                )
              })}
            </Stack>
          </Box>

          <Box>
            <Typography variant="overline" color="text.secondary">Payment</Typography>
            <Stack gap={0.5} sx={{ mt: 0.5 }}>
              {[
                ['Subtotal', order.subtotal], ['Delivery fee', order.deliveryFee], ['Processing fee', order.processingFee],
                ['Tax', order.tax], ['Tip (driver)', order.tip],
              ].map(([l, v]) => (
                <Stack key={l} direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">{l}</Typography>
                  <Typography variant="body2">{money(v)}</Typography>
                </Stack>
              ))}
              <Divider sx={{ my: 0.5 }} />
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body1" sx={{ fontWeight: 700 }}>Total</Typography>
                <Typography variant="body1" sx={{ fontWeight: 700 }}>{money(order.total)}</Typography>
              </Stack>
            </Stack>
          </Box>

          <Divider />

          <Stack direction="row" gap={1}>
            <Button
              color="error" variant="outlined" size="small"
              disabled={cancelling || ['DELIVERED', 'CANCELLED'].includes(order.status)}
              onClick={() => onCancel(order)}
              sx={{ textTransform: 'none' }}
            >
              Cancel delivery
            </Button>
            <Box sx={{ flex: 1 }} />
            <Button
              variant="contained" size="small"
              disabled={dispatching || !!order.fdOrderId || order.orderKind !== 'DELIVERY'}
              onClick={() => onDispatch(order)}
              startIcon={dispatching ? <CircularProgress size={14} sx={{ color: 'inherit' }} /> : null}
              sx={{ textTransform: 'none' }}
            >
              {order.fdOrderId ? 'Already dispatched' : dispatching ? 'Sending…' : 'Send to First Delivery'}
            </Button>
          </Stack>
        </Stack>
      </Box>
    </Drawer>
  )
}

export default function CourierOrders() {
  const { MAPBOX_TOKEN } = ConfigurableValues()
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState('LIVE')
  const [page, setPage] = useState(1)
  const [openId, setOpenId] = useState(null)
  const [msg, setMsg] = useState(null)
  const [cancelConfirm, setCancelConfirm] = useState(null)

  // Fetched once, unfiltered — everything else (tab counts, search, sort) happens
  // client-side, the same trade Dispatch makes for its own active-orders view.
  const { data, loading, refetch } = useQuery(COURIER_ORDERS, {
    variables: { status: null, limit: FETCH_ALL_LIMIT, offset: 0 },
    fetchPolicy: 'cache-and-network',
    pollInterval: 30000,
  })
  const health = useQuery(COURIER_HEALTH, { fetchPolicy: 'network-only', pollInterval: 60000 })

  const [dispatch, dispatchState] = useMutation(DISPATCH)
  const [cancel, cancelState] = useMutation(CANCEL)

  const allOrders = data?.courierOrders?.orders ?? []

  const healthData = useMemo(() => {
    try { return JSON.parse(health.data?.courierHealth || '{}') } catch { return {} }
  }, [health.data])
  const stalls = healthData.stalls ?? []
  // One order can trip more than one stall rule at once (e.g. both "no driver"
  // and "past due") — stalls is one entry per RULE, not per order, so this groups
  // by order to get an honest "N orders need attention" count instead of
  // counting rule-fires as if each were its own order.
  const stallByOrder = useMemo(() => {
    const m = {}
    for (const s of stalls) {
      if (!m[s.orderId]) m[s.orderId] = { externalOrderNumber: s.externalOrderNumber || s.orderId, labels: [] }
      m[s.orderId].labels.push(s.label)
    }
    return m
  }, [stalls])

  const counts = useMemo(() => ({
    LIVE: allOrders.length,
    DISPATCHING: allOrders.filter((o) => o.status === 'DISPATCHING').length,
    READY: allOrders.filter((o) => o.status === 'READY').length,
    DELIVERED: allOrders.filter((o) => o.status === 'DELIVERED').length,
    FAILED: allOrders.filter((o) => o.status === 'FAILED').length,
    CANCELLED: allOrders.filter((o) => o.status === 'CANCELLED').length,
  }), [allOrders])

  const filtered = useMemo(() => {
    const wanted = STATUS_FILTERS.find((f) => f.key === statusFilter)?.status ?? null
    let list = wanted ? allOrders.filter((o) => o.status === wanted) : allOrders
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase()
      list = list.filter((o) =>
        o.externalOrderNumber?.toLowerCase().includes(q) ||
        o.merchantName?.toLowerCase().includes(q) ||
        o.merchantProvider?.toLowerCase().includes(q) ||
        o.customerName?.toLowerCase().includes(q) ||
        o.customerPhone?.toLowerCase().includes(q) ||
        o.dropoffAddress?.toLowerCase().includes(q) ||
        o.fdDriverName?.toLowerCase().includes(q) ||
        o.status?.toLowerCase().includes(q))
    }
    // Stalled first, then newest — a monitoring screen that buries the broken
    // order under twenty healthy ones has failed at its one job.
    return [...list].sort((a, b) => {
      const as = stallByOrder[a._id] ? 1 : 0, bs = stallByOrder[b._id] ? 1 : 0
      if (as !== bs) return bs - as
      return new Date(b.createdAt) - new Date(a.createdAt)
    })
  }, [allOrders, statusFilter, searchQuery, stallByOrder])

  const open = allOrders.find((o) => o._id === openId) || null

  const run = async (fn, label, { closeOnSuccess } = {}) => {
    setMsg(null)
    try {
      const res = await fn()
      const payload = res?.data?.courierDispatchOrder || res?.data?.courierCancelOrder
      if (payload?.ok === false) setMsg({ severity: 'error', text: `${label} failed: ${payload.reason}` })
      else if (payload?.skipped) setMsg({ severity: 'info', text: `${label}: ${payload.reason}` })
      else setMsg({ severity: 'success', text: `${label} done${payload?.fdOrderId ? ` — First Delivery ${payload.fdOrderId}` : ''}` })
      await refetch()
      await health.refetch()
      if (closeOnSuccess && payload?.ok !== false) setOpenId(null)
    } catch (e) {
      setMsg({ severity: 'error', text: `${label} failed: ${e.message}` })
    }
  }

  const columns = [
    {
      name: '', width: '40px',
      cell: (r) => (stallByOrder[r._id] ? (
        <Tooltip title={stallByOrder[r._id].labels.join(' · ')}>
          <WarningAmberIcon fontSize="small" sx={{ color: '#b45309' }} />
        </Tooltip>
      ) : null),
    },
    { name: 'Partner #', width: '110px', cell: (r) => <strong>{r.externalOrderNumber || r._id}</strong> },
    {
      name: 'Merchant', width: '160px',
      cell: (r) => (
        <Box sx={{ lineHeight: 1.3 }}>
          <div>{r.merchantName || '—'}</div>
          <div style={{ fontSize: '0.7rem', color: '#94a3b8' }}>{PROVIDER_LABELS[r.merchantProvider] || r.merchantProvider || '—'}</div>
        </Box>
      ),
    },
    { name: 'Customer', width: '150px', cell: (r) => <span>{r.customerName || '—'}</span> },
    { name: 'Status', width: '135px', cell: (r) => <StatusChip status={r.status} /> },
    {
      name: 'Driver', width: '140px',
      cell: (r) => (r.fdDriverName
        ? r.fdDriverName
        : r.fdOrderId ? <span style={{ color: '#b45309' }}>none yet</span> : <span style={{ color: '#94a3b8' }}>—</span>),
    },
    { name: 'Due', width: '160px', cell: (r) => <span style={{ fontSize: 13 }}>{when(r.promiseTime)}</span> },
    {
      name: 'Last FD event', width: '120px',
      cell: (r) => {
        const m = minsSince(r.lastFdEventAt)
        if (m == null) return <span style={{ color: '#94a3b8' }}>never</span>
        return <span style={{ color: m > 15 ? '#b45309' : '#475569' }}>{m}m ago</span>
      },
    },
    { name: 'Total', width: '90px', cell: (r) => money(r.total) },
  ]

  return (
    <Container maxWidth={false} sx={{ py: 3 }}>
      <Paper sx={{ p: 2, mb: 2, borderRadius: 2 }}>
        <Stack direction="row" alignItems="center" gap={2} flexWrap="wrap">
          <Stack direction="row" alignItems="center" gap={1}>
            <LocalShippingIcon sx={{ color: '#FF3008' }} />
            <Typography variant="h6" sx={{ fontWeight: 700 }}>Courier Orders</Typography>
          </Stack>
          <TextField
            size="small" placeholder="Search order #, merchant, customer, address, driver…"
            value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
            sx={{ flex: 1, minWidth: 280 }}
          />
          {healthData.ingest && (
            <Chip
              size="small"
              label={`mailbox ${healthData.ingest.enabled ? (healthData.ingest.stale ? 'STALE' : 'ok') : 'off'}`}
              sx={{ bgcolor: healthData.ingest.enabled && healthData.ingest.stale ? '#fee2e2' : '#f1f5f9', fontWeight: 600 }}
            />
          )}
          {healthData.worker && (
            <Chip
              size="small"
              label={`worker ${healthData.worker.enabled ? (healthData.worker.stale ? 'STALE' : 'ok') : 'off'}`}
              sx={{ bgcolor: healthData.worker.enabled && healthData.worker.stale ? '#fee2e2' : '#f1f5f9', fontWeight: 600 }}
            />
          )}
          <Tooltip title="Refresh"><IconButton onClick={() => { refetch(); health.refetch() }} size="small"><RefreshIcon /></IconButton></Tooltip>
          {loading && <CircularProgress size={18} />}
        </Stack>

        <ToggleButtonGroup
          value={statusFilter} exclusive size="small"
          onChange={(_, v) => { if (v) { setStatusFilter(v); setPage(1) } }}
          sx={{
            mt: 2, flexWrap: 'wrap',
            '& .MuiToggleButton-root': { textTransform: 'none', fontWeight: 600, fontSize: '0.78rem', py: 0.4, color: '#475569', backgroundColor: '#fff', border: '1px solid #e5e7eb' },
            '& .MuiToggleButton-root.Mui-selected': { color: '#fff', backgroundColor: '#1e40af', '&:hover': { backgroundColor: '#1e3a8a' } },
          }}
        >
          {STATUS_FILTERS.map((f) => (
            <ToggleButton key={f.key} value={f.key}>
              {f.label}
              <Chip size="small" label={counts[f.key] ?? 0} sx={{ ml: 1, height: 18, fontSize: '0.68rem', fontWeight: 700 }} />
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Paper>

      {Object.keys(stallByOrder).length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <strong>{Object.keys(stallByOrder).length} order{Object.keys(stallByOrder).length > 1 ? 's' : ''} need attention:</strong>
          <ul style={{ margin: '6px 0 0 18px' }}>
            {Object.values(stallByOrder).slice(0, 6).map((o, i) => <li key={i}>#{o.externalOrderNumber} — {o.labels.join('; ')}</li>)}
          </ul>
        </Alert>
      )}
      {msg && <Alert severity={msg.severity} sx={{ mb: 2 }} onClose={() => setMsg(null)}>{msg.text}</Alert>}

      <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <DataTable
          columns={columns}
          data={filtered}
          progressPending={loading}
          progressComponent={<Box sx={{ py: 4 }}><CircularProgress /></Box>}
          pagination
          paginationPerPage={PAGE_SIZE}
          paginationDefaultPage={page}
          onChangePage={setPage}
          paginationRowsPerPageOptions={[10, 25, 50, 100]}
          noDataComponent={<Box sx={{ py: 5, color: '#64748b' }}>No courier orders here</Box>}
          conditionalRowStyles={[{ when: (r) => !!stallByOrder[r._id], style: { backgroundColor: '#fffbeb' } }]}
          onRowClicked={(r) => { setOpenId(r._id); setMsg(null) }}
          pointerOnHover highlightOnHover dense
        />
      </Paper>

      <OrderDrawer
        order={open}
        stallLabels={open ? stallByOrder[open._id]?.labels : null}
        mapboxToken={MAPBOX_TOKEN}
        onClose={() => setOpenId(null)}
        onDispatch={(o) => run(() => dispatch({ variables: { id: o._id } }), 'Dispatch')}
        onCancel={setCancelConfirm}
        dispatching={dispatchState.loading}
        cancelling={cancelState.loading}
      />

      <Dialog open={!!cancelConfirm} onClose={() => !cancelState.loading && setCancelConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Cancel this delivery?</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: '0.9rem' }}>
            Order <strong>#{cancelConfirm?.externalOrderNumber}</strong> from <strong>{cancelConfirm?.merchantName}</strong> will
            be cancelled. If First Delivery already has a driver en route, cancellation only takes effect once FD confirms it —
            the local status will not flip early.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setCancelConfirm(null)} disabled={cancelState.loading} sx={{ textTransform: 'none' }}>Keep it</Button>
          <Button
            variant="contained" color="error" disableElevation disabled={cancelState.loading}
            startIcon={cancelState.loading ? <CircularProgress size={14} sx={{ color: 'inherit' }} /> : null}
            onClick={() => {
              const target = cancelConfirm
              run(() => cancel({ variables: { id: target._id, reason: 'Cancelled from admin' } }), 'Cancel', { closeOnSuccess: true })
              setCancelConfirm(null)
            }}
            sx={{ textTransform: 'none' }}
          >
            {cancelState.loading ? 'Cancelling…' : 'Cancel delivery'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  )
}
